import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Shield, ScrollText, Sparkles, Fingerprint, ClipboardCheck } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { TID } from "../constants/testIds";

/**
 * TermsAcceptModal
 * Shown once per user on first login. Records consent to Terms of Service,
 * Privacy Policy, and PropHR AI Advisory Disclaimer with a fingerprint.
 * After acceptance, `user.terms_accepted_at` is populated and the modal
 * never shows again for this user.
 */
export default function TermsAcceptModal() {
  const { user, refreshMe } = useAuth();
  const [agreeAll, setAgreeAll] = useState(false);
  const [busy, setBusy] = useState(false);

  const alreadyAccepted = !!user?.terms_accepted_at;
  if (alreadyAccepted) return null;

  const accept = async () => {
    if (!agreeAll) return toast.error("Please read and confirm the acknowledgment first.");
    setBusy(true);
    try {
      await api.post("/auth/accept-terms", { version: "2026.02.1" });
      await refreshMe();
      toast.success("Thank you. Your acceptance has been recorded.");
    } catch {
      toast.error("Could not record your acceptance. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 z-[100] bg-slate-950/85 flex items-center justify-center p-4"
      data-testid="terms-modal"
    >
      <motion.div
        initial={{ y: 16, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: "spring", stiffness: 260, damping: 26 }}
        className="w-full max-w-2xl bg-white border border-slate-200 shadow-2xl max-h-[92vh] overflow-hidden flex flex-col"
      >
        <div className="p-5 border-b border-slate-200 bg-slate-950 text-slate-100">
          <div className="flex items-center gap-2 mb-1">
            <Shield size={16} strokeWidth={2} className="text-blue-300"/>
            <div className="eyebrow text-blue-300">Welcome to PropHR</div>
          </div>
          <div className="font-editorial text-2xl md:text-3xl">Terms, privacy, and AI advisory acknowledgment</div>
          <div className="text-xs text-slate-400 mt-1 font-mono">Signing in as {user?.name} , {user?.email}</div>
        </div>

        <div className="p-6 overflow-y-auto flex-1 space-y-5 text-sm text-slate-800 leading-relaxed">
          <Section icon={ScrollText} title="Terms of Service">
            You are accessing a confidential enterprise workspace operated by your employer through PropHR, Inc.
            Access is granted for the sole purpose of good-faith employee relations activities. Any unauthorized use,
            data exfiltration, or misrepresentation may result in disciplinary action, termination, and civil or criminal liability.
          </Section>

          <Section icon={Fingerprint} title="Privacy Policy & Data Processing">
            All actions you take on PropHR (login, cases filed, files uploaded, policy signatures, workflow transitions)
            are recorded in a tamper-evident audit log for the retention period defined by your organization
            (typically 7 years). We process personal data under GDPR, CCPA, and EEOC requirements. Where you file a case,
            the reporter identity is masked to managers and other employees unless disclosed by law or subpoena.
          </Section>

          <Section icon={Sparkles} title="PropHR AI Advisory Disclaimer">
            PropHR uses generative AI (Sentinel Analytics, Dual Perspective, and Enterprise Intelligence) to help
            triage, summarize, and highlight patterns across cases and policies. AI outputs are <span className="font-semibold">advisory only</span>.
            All disciplinary, findings, and policy decisions require independent human review. You should not treat AI
            outputs as legal advice or as a substitute for qualified professional judgment.
          </Section>

          <Section icon={ClipboardCheck} title="Anonymous reporting">
            You may file certain cases anonymously. When you do, your identity is masked from managers and other
            employees. Your IP address, browser signature, and typed legal signature are stored in a sealed record accessible
            only to Company Admins under legal process. Deliberately false reports may result in disciplinary action.
          </Section>

          <label className="flex items-start gap-3 pt-3 mt-2 border-t border-slate-200 cursor-pointer">
            <input
              type="checkbox"
              checked={agreeAll}
              onChange={(e) => setAgreeAll(e.target.checked)}
              className="mt-1 w-4 h-4"
              data-testid={TID.termsAgreeCheckbox}
            />
            <span className="text-[13px] leading-relaxed text-slate-900">
              <span className="font-semibold">I have read and agree</span> to the Terms of Service, Privacy Policy,
              and the PropHR AI Advisory Disclaimer. I understand this acknowledgment is fingerprinted, timestamped,
              and stored in the audit trail.
            </span>
          </label>
        </div>

        <div className="p-4 border-t border-slate-200 bg-slate-50 flex items-center justify-between gap-2">
          <div className="text-[11px] font-mono text-slate-500">
            v2026.02.1 , fingerprint recorded on accept
          </div>
          <button
            className="btn-primary"
            onClick={accept}
            disabled={!agreeAll || busy}
            data-testid={TID.termsAcceptBtn}
          >
            {busy ? "Recording..." : "Accept & continue"}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-1">
        <Icon size={14} strokeWidth={2} className="text-slate-700"/>
        <span className="eyebrow">{title}</span>
      </div>
      <p className="text-[13px] text-slate-700">{children}</p>
    </div>
  );
}
